/**
 * BattlePlanner.ts
 * CPU opponent brain. It turns a compact battlefield snapshot into enemy
 * commands using weakness detection, strategy modes, and bounded randomness.
 */

import type {
  BattleBuildingSnapshot,
  BattleSnapshot,
  BattleUnitSnapshot,
  EnemyCommand,
  EnemyType,
  GridPosition,
  SpawnEdge,
} from '../types';

type StrategicPlan =
  | 'bleedEconomy'
  | 'drainSignal'
  | 'captureBeacon'
  | 'denySongDrop'
  | 'raidExpansion'
  | 'huntRecruiters'
  | 'pressureRecruitment'
  | 'stallSongDrop'
  | 'breakDefence'
  | 'splitAttention'
  | 'feintThenSpike';

interface PlannerTuning {
  maxCommands: number;
  spawnBudgetShare: number;
  earlySpawnBudgetShare: number;
  lowHpRetreatRatio: number;
  songDropDangerSignal: number;
  edgeRepeatPenalty: number;
  watchedEdgePenalty: number;
  hiddenPressureBonus: number;
  adaptationMemory: number;
  earlyGraceWaves: number;
  songDropDenyProgress: number;
  captureCommitSignal: number;
  captureCommitProgress: number;
}

interface PlayerWeaknessReport {
  lowSales: number;
  lowGear: number;
  floatingResources: number;
  exposedFans: number;
  activeMining: number;
  exposedBuildSite: number;
  exposedGearSite: number;
  exposedEconomy: number;
  exposedBeacon: number;
  exposedRecruitment: number;
  isolatedRecruiter: number;
  weakAntiSwarm: number;
  overcommittedBand: number;
  fogBlindSpot: number;
  turtle: number;
  collapseRisk: number;
}

interface ScoredPlan {
  plan: StrategicPlan;
  score: number;
  target: BattleBuildingSnapshot | BattleUnitSnapshot | null;
  weakness: keyof PlayerWeaknessReport;
}

const DEFAULT_TUNING: PlannerTuning = {
  maxCommands: 8,
  spawnBudgetShare: 0.75,
  earlySpawnBudgetShare: 0.32,
  lowHpRetreatRatio: 0.3,
  songDropDangerSignal: 75,
  edgeRepeatPenalty: 18,
  watchedEdgePenalty: 28,
  hiddenPressureBonus: 14,
  adaptationMemory: 4,
  earlyGraceWaves: 2,
  songDropDenyProgress: 35,
  captureCommitSignal: 25,
  captureCommitProgress: 0.2,
};

const ENEMY_COST: Record<EnemyType, number> = {
  GenZSwarm: 30,
  StreamingMogul: 120,
  BadManager: 90,
  TributeAct: 180,
  MusicPress: 70,
  FilePirate: 95,
  RivalVenueBuilder: 125,
};

const PLAN_UNIT: Record<StrategicPlan, EnemyType> = {
  bleedEconomy: 'GenZSwarm',
  drainSignal: 'StreamingMogul',
  captureBeacon: 'TributeAct',
  denySongDrop: 'StreamingMogul',
  raidExpansion: 'RivalVenueBuilder',
  huntRecruiters: 'BadManager',
  pressureRecruitment: 'TributeAct',
  stallSongDrop: 'FilePirate',
  breakDefence: 'TributeAct',
  splitAttention: 'GenZSwarm',
  feintThenSpike: 'TributeAct',
};

const EDGES: SpawnEdge[] = ['north', 'south', 'east', 'west'];

export class BattlePlanner {
  private lastSpawnEdge: SpawnEdge | null = null;
  private recentSpawnEdges: SpawnEdge[] = [];
  private lastPlan: StrategicPlan | null = null;

  constructor(private readonly tuning: PlannerTuning = DEFAULT_TUNING) {}

  decide(snapshot: BattleSnapshot): EnemyCommand[] {
    const commands: EnemyCommand[] = [];
    const weaknesses = this.detectWeaknesses(snapshot);

    commands.push(...this.retreatWounded(snapshot));
    commands.push(...this.directIdleEnemies(snapshot));

    const plans = this.scorePlans(snapshot, weaknesses);
    const chosen = this.pickPlan(plans, snapshot.tick);
    if (chosen) {
      commands.push(...this.commandsForPlan(snapshot, chosen, weaknesses));
      this.lastPlan = chosen.plan;
    }

    return commands.slice(0, this.tuning.maxCommands);
  }

  private retreatWounded(snapshot: BattleSnapshot): EnemyCommand[] {
    return snapshot.enemyUnits
      .filter((unit) => this.shouldRetreat(unit))
      .slice(0, 2)
      .map((unit) => ({ type: 'retreat', unitId: unit.id }));
  }

  private directIdleEnemies(snapshot: BattleSnapshot): EnemyCommand[] {
    const commands: EnemyCommand[] = [];
    for (const unit of snapshot.enemyUnits.filter((candidate) => !this.shouldRetreat(candidate)).slice(0, 3)) {
      const target = this.bestTargetForUnit(snapshot, unit);
      if (!target) continue;
      commands.push({
        type: 'move',
        unitId: unit.id,
        target: { col: target.col, row: target.row },
      });
    }
    return commands;
  }

  private shouldRetreat(unit: BattleUnitSnapshot): boolean {
    const maxHp = unit.maxHp ?? Math.max(unit.hp, 1);
    return unit.hp / maxHp <= this.tuning.lowHpRetreatRatio;
  }

  private detectWeaknesses(snapshot: BattleSnapshot): PlayerWeaknessReport {
    const beacon = this.findBuilding(snapshot, 'FutureBeacon');
    const economyTarget = this.bestBuilding(snapshot, ['FlyerPost', 'MerchStall', 'Pub']);
    const recruitmentTarget = this.findBuilding(snapshot, 'RehearsalSpace');
    const ali = this.bestPlayerUnit(snapshot, ['promoter']);
    const fanTarget = this.bestPlayerUnit(snapshot, ['fan']);
    const miningFanTarget = this.bestMiningFan(snapshot);
    const buildSiteTarget = this.bestBuildSite(snapshot);
    const gearSiteTarget = this.bestGearSite(snapshot);

    const sales = snapshot.resources.sales ?? 80;
    const gear = snapshot.resources.gear ?? 40;
    const reputation = snapshot.resources.reputation ?? 120;
    const livingPlayerUnits = snapshot.playerUnits.filter((unit) => unit.hp > 0);
    const protectorCount = livingPlayerUnits.filter((unit) => unit.type === 'protector').length;
    const engineCount = livingPlayerUnits.filter((unit) => unit.type === 'engine').length;
    const lowHpUnits = livingPlayerUnits.filter((unit) => {
      const maxHp = unit.maxHp ?? unit.hp;
      return maxHp > 0 && unit.hp / maxHp < 0.45;
    }).length;
    const watchedEdges = EDGES.filter((edge) => this.edgeVisibility(snapshot, edge) > 0.65).length;
    const defenderStrength = livingPlayerUnits.reduce((sum, unit) => sum + this.unitThreat(unit), 0);

    return {
      lowSales: clampScore((45 - sales) * 0.75),
      lowGear: clampScore((25 - gear) * 0.9),
      floatingResources: clampScore((sales - 180) * 0.16 + (reputation - 260) * 0.08 + (gear - 80) * 0.22),
      exposedFans: clampScore(this.exposureScore(snapshot, fanTarget)),
      activeMining: clampScore((snapshot.playerUnits.filter((unit) => unit.type === 'fan' && unit.isMining).length * 11) +
        this.exposureScore(snapshot, miningFanTarget)),
      exposedBuildSite: clampScore(this.expansionSitePressure(snapshot, buildSiteTarget)),
      exposedGearSite: clampScore(this.expansionSitePressure(snapshot, gearSiteTarget)),
      exposedEconomy: clampScore(this.exposureScore(snapshot, economyTarget)),
      exposedBeacon: clampScore(beacon ? this.exposureScore(snapshot, beacon) : 0),
      exposedRecruitment: clampScore(recruitmentTarget ? this.exposureScore(snapshot, recruitmentTarget) : 0),
      isolatedRecruiter: ali ? clampScore(this.isolationScore(snapshot, ali)) : 0,
      weakAntiSwarm: clampScore((2 - protectorCount) * 12 + (1 - engineCount) * 6),
      overcommittedBand: clampScore(lowHpUnits * 8 + Math.max(0, 4 - livingPlayerUnits.length) * 5),
      fogBlindSpot: clampScore(this.fogOpportunity(snapshot) + Math.max(0, 2 - watchedEdges) * 5),
      turtle: clampScore(defenderStrength * 0.75 + snapshot.beacon.hp * 0.1 + Math.max(0, sales - 120) * 0.08),
      collapseRisk: clampScore(lowHpUnits * 10 + Math.max(0, 3 - livingPlayerUnits.length) * 12 - snapshot.resources.enemyBudget * 0.02),
    };
  }

  private scorePlans(snapshot: BattleSnapshot, weaknesses: PlayerWeaknessReport): ScoredPlan[] {
    const beacon = this.findBuilding(snapshot, 'FutureBeacon');
    const economyTarget = this.bestBuilding(snapshot, ['FlyerPost', 'MerchStall', 'Pub']);
    const ali = this.bestPlayerUnit(snapshot, ['promoter']);
    const rehearsal = this.findBuilding(snapshot, 'RehearsalSpace');
    const expansionTarget = this.bestExpansionTarget(snapshot);

    const signalDanger = Math.max(0, this.tuning.songDropDangerSignal - snapshot.beacon.signal);
    const songDropDanger = Math.max(0, snapshot.beacon.signal - this.tuning.songDropDangerSignal);
    const songDropProgress = snapshot.beacon.songDropProgress ?? 0;
    const captureProgress = snapshot.beacon.captureProgress ?? 0;
    const fanbasePressure = this.fanbasePressure(snapshot);
    const hiddenPressure = this.hiddenPressure(snapshot);
    const waveAggression = Math.min(35, snapshot.wave * 5);
    const mercy = weaknesses.collapseRisk > 22 ? 0.72 : 1;
    const onboarding = this.onboardingPressure(snapshot);

    const plans: ScoredPlan[] = [
      {
        plan: 'bleedEconomy',
        target: economyTarget,
        weakness: 'exposedEconomy',
        score:
          (35 +
            weaknesses.lowSales +
            weaknesses.exposedEconomy +
            weaknesses.weakAntiSwarm +
            fanbasePressure +
            waveAggression -
            this.repeatPlanPenalty('bleedEconomy')) *
          mercy *
          onboarding.softPressure,
      },
      {
        plan: 'drainSignal',
        target: beacon,
        weakness: 'exposedBeacon',
        score:
          (28 +
            signalDanger +
            hiddenPressure +
            weaknesses.exposedBeacon +
            this.mogulNeed(snapshot) +
            waveAggression * 0.8 -
            this.repeatPlanPenalty('drainSignal')) *
          mercy *
          onboarding.heavyPressure,
      },
      {
        plan: 'captureBeacon',
        target: beacon,
        weakness: 'exposedBeacon',
        score:
          snapshot.beacon.signal <= this.tuning.captureCommitSignal || captureProgress >= this.tuning.captureCommitProgress
            ? (70 +
                Math.max(0, this.tuning.captureCommitSignal - snapshot.beacon.signal) * 1.3 +
                captureProgress * 90 +
                weaknesses.exposedBeacon +
                waveAggression) *
              onboarding.heavyPressure
            : 0,
      },
      {
        plan: 'denySongDrop',
        target: beacon,
        weakness: 'exposedBeacon',
        score:
          snapshot.beacon.signal >= this.tuning.songDropDangerSignal ||
          songDropProgress >= this.tuning.songDropDenyProgress
            ? (54 +
                songDropDanger +
                songDropProgress * 0.8 +
                this.mogulNeed(snapshot) +
                weaknesses.fogBlindSpot +
                waveAggression * 0.7) *
              mercy *
              onboarding.heavyPressure
            : 0,
      },
      {
        plan: 'raidExpansion',
        target: expansionTarget,
        weakness: 'exposedBuildSite',
        score:
          (32 +
            weaknesses.exposedFans +
            weaknesses.activeMining +
            weaknesses.exposedBuildSite +
            weaknesses.exposedGearSite +
            weaknesses.lowGear +
            waveAggression * 0.8) *
          mercy *
          onboarding.softPressure,
      },
      {
        plan: 'huntRecruiters',
        target: ali,
        weakness: 'isolatedRecruiter',
        score: ali
          ? (32 + weaknesses.isolatedRecruiter + weaknesses.fogBlindSpot * 0.7 + waveAggression) *
            mercy *
            onboarding.softPressure
          : 4,
      },
      {
        plan: 'pressureRecruitment',
        target: rehearsal,
        weakness: 'exposedRecruitment',
        score: rehearsal
          ? (24 + weaknesses.exposedRecruitment + weaknesses.lowGear + waveAggression * 1.1) *
            mercy *
            onboarding.heavyPressure
          : 0,
      },
      {
        plan: 'stallSongDrop',
        target: economyTarget ?? beacon,
        weakness: 'fogBlindSpot',
        score:
          songDropDanger > 0
            ? (44 + songDropDanger + weaknesses.fogBlindSpot + waveAggression) * mercy * onboarding.softPressure
            : 0,
      },
      {
        plan: 'breakDefence',
        target: beacon,
        weakness: 'turtle',
        score:
          (18 +
            Math.max(0, 40 - snapshot.beacon.hp) +
            weaknesses.turtle +
            weaknesses.floatingResources +
            waveAggression * 0.9) *
          onboarding.heavyPressure,
      },
      {
        plan: 'splitAttention',
        target: economyTarget ?? ali ?? beacon,
        weakness: 'fogBlindSpot',
        score:
          (24 +
            weaknesses.fogBlindSpot +
            weaknesses.weakAntiSwarm +
            weaknesses.exposedEconomy * 0.55 +
            waveAggression) *
          mercy *
          onboarding.trickPressure,
      },
      {
        plan: 'feintThenSpike',
        target: beacon ?? rehearsal,
        weakness: 'floatingResources',
        score:
          (16 +
            weaknesses.floatingResources +
            weaknesses.turtle +
            weaknesses.fogBlindSpot * 0.8 +
            waveAggression * 0.9) *
          onboarding.trickPressure,
      },
    ];

    return plans.sort((a, b) => b.score - a.score);
  }

  private commandsForPlan(
    snapshot: BattleSnapshot,
    scored: ScoredPlan,
    weaknesses: PlayerWeaknessReport,
  ): EnemyCommand[] {
    const commands: EnemyCommand[] = [];
    const target = scored.target;
    if (target) {
      commands.push({ type: 'priority', targetId: target.id, priority: 'high' });
    }

    if (scored.plan === 'splitAttention') {
      return commands.concat(this.splitAttentionCommands(snapshot, target, weaknesses));
    }

    if (scored.plan === 'feintThenSpike') {
      return commands.concat(this.feintThenSpikeCommands(snapshot, target, weaknesses));
    }

    if (scored.plan === 'captureBeacon') {
      return commands.concat(this.captureBeaconCommands(snapshot, target, weaknesses));
    }

    if (scored.plan === 'denySongDrop') {
      return commands.concat(this.denySongDropCommands(snapshot, target, weaknesses));
    }

    const enemyType = PLAN_UNIT[scored.plan];
    const budget = this.planBudget(snapshot, weaknesses);
    const count = this.spawnCount(enemyType, budget, snapshot.wave, scored.plan);
    if (count > 0) {
      const edge = this.chooseSpawnEdge(snapshot, target, scored.plan);
      this.recordSpawnEdge(edge);
      commands.push({ type: 'spawn', enemyType, count, fromEdge: edge });
    }

    if (scored.plan === 'drainSignal') {
      const moguls = snapshot.enemyUnits.filter((unit) => unit.type === 'StreamingMogul');
      const beacon = this.findBuilding(snapshot, 'FutureBeacon');
      for (const unit of moguls.slice(0, 2)) {
        if (!beacon) continue;
        commands.push({ type: 'move', unitId: unit.id, target: { col: beacon.col, row: beacon.row } });
      }
    }

    if (scored.plan === 'raidExpansion') {
      for (const unit of snapshot.enemyUnits.filter((unit) => unit.type === 'GenZSwarm' || unit.type === 'BadManager').slice(0, 3)) {
        if (!target) continue;
        commands.push({ type: 'move', unitId: unit.id, target: { col: target.col, row: target.row } });
      }
    }

    if (scored.plan === 'stallSongDrop') {
      for (const unit of snapshot.enemyUnits.slice(0, 2)) {
        commands.push({ type: 'retreat', unitId: unit.id });
      }
    }

    return commands;
  }

  private captureBeaconCommands(
    snapshot: BattleSnapshot,
    target: BattleBuildingSnapshot | BattleUnitSnapshot | null,
    weaknesses: PlayerWeaknessReport,
  ): EnemyCommand[] {
    const commands: EnemyCommand[] = [];
    const beacon = target ?? this.findBuilding(snapshot, 'FutureBeacon');
    if (!beacon) return commands;

    const budget = this.planBudget(snapshot, weaknesses);
    const tributeCount = this.spawnCount('TributeAct', Math.floor(budget * 0.62), snapshot.wave, 'captureBeacon');
    const mogulCount = this.spawnCount('StreamingMogul', Math.floor(budget * 0.38), snapshot.wave, 'captureBeacon');
    const edge = this.chooseSpawnEdge(snapshot, beacon, 'captureBeacon');

    if (tributeCount > 0) {
      this.recordSpawnEdge(edge);
      commands.push({ type: 'spawn', enemyType: 'TributeAct', count: tributeCount, fromEdge: edge });
    }
    if (mogulCount > 0) {
      const secondEdge = this.chooseSpawnEdge(snapshot, beacon, 'captureBeacon', edge);
      this.recordSpawnEdge(secondEdge);
      commands.push({ type: 'spawn', enemyType: 'StreamingMogul', count: mogulCount, fromEdge: secondEdge });
    }

    for (const unit of snapshot.enemyUnits.slice(0, 5)) {
      commands.push({ type: 'move', unitId: unit.id, target: { col: beacon.col, row: beacon.row } });
    }
    return commands;
  }

  private denySongDropCommands(
    snapshot: BattleSnapshot,
    target: BattleBuildingSnapshot | BattleUnitSnapshot | null,
    weaknesses: PlayerWeaknessReport,
  ): EnemyCommand[] {
    const commands: EnemyCommand[] = [];
    const beacon = target ?? this.findBuilding(snapshot, 'FutureBeacon');
    if (!beacon) return commands;

    const budget = this.planBudget(snapshot, weaknesses);
    const mogulCount = this.spawnCount('StreamingMogul', Math.floor(budget * 0.7), snapshot.wave, 'denySongDrop');
    const swarmCount = this.spawnCount('GenZSwarm', Math.floor(budget * 0.3), snapshot.wave, 'denySongDrop');
    const edge = this.chooseSpawnEdge(snapshot, beacon, 'denySongDrop');

    if (mogulCount > 0) {
      this.recordSpawnEdge(edge);
      commands.push({ type: 'spawn', enemyType: 'StreamingMogul', count: mogulCount, fromEdge: edge });
    }
    if (swarmCount > 0) {
      const flank = this.chooseSpawnEdge(snapshot, beacon, 'denySongDrop', edge);
      this.recordSpawnEdge(flank);
      commands.push({ type: 'spawn', enemyType: 'GenZSwarm', count: Math.min(4, swarmCount), fromEdge: flank });
    }

    for (const unit of snapshot.enemyUnits.filter((unit) => unit.type === 'StreamingMogul').slice(0, 3)) {
      commands.push({ type: 'move', unitId: unit.id, target: { col: beacon.col, row: beacon.row } });
    }
    return commands;
  }

  private splitAttentionCommands(
    snapshot: BattleSnapshot,
    target: BattleBuildingSnapshot | BattleUnitSnapshot | null,
    weaknesses: PlayerWeaknessReport,
  ): EnemyCommand[] {
    const commands: EnemyCommand[] = [];
    const budget = this.planBudget(snapshot, weaknesses);
    const firstCount = this.spawnCount('GenZSwarm', Math.floor(budget * 0.55), snapshot.wave, 'splitAttention');
    const secondCount = this.spawnCount('BadManager', Math.floor(budget * 0.45), snapshot.wave, 'splitAttention');
    const firstEdge = this.chooseSpawnEdge(snapshot, target, 'splitAttention');
    const secondEdge = this.chooseSpawnEdge(snapshot, target, 'splitAttention', firstEdge);

    if (firstCount > 0) {
      this.recordSpawnEdge(firstEdge);
      commands.push({ type: 'spawn', enemyType: 'GenZSwarm', count: firstCount, fromEdge: firstEdge });
    }
    if (secondCount > 0) {
      this.recordSpawnEdge(secondEdge);
      commands.push({ type: 'spawn', enemyType: 'BadManager', count: secondCount, fromEdge: secondEdge });
    }
    return commands;
  }

  private feintThenSpikeCommands(
    snapshot: BattleSnapshot,
    target: BattleBuildingSnapshot | BattleUnitSnapshot | null,
    weaknesses: PlayerWeaknessReport,
  ): EnemyCommand[] {
    const commands: EnemyCommand[] = [];
    const budget = this.planBudget(snapshot, weaknesses);
    const feintBudget = Math.floor(budget * 0.25);
    const spikeBudget = Math.floor(budget * 0.75);
    const feintEdge = this.mostWatchedEdge(snapshot) ?? this.chooseSpawnEdge(snapshot, target, 'feintThenSpike');
    const spikeEdge = this.chooseSpawnEdge(snapshot, target, 'feintThenSpike', feintEdge);
    const feintCount = this.spawnCount('GenZSwarm', feintBudget, snapshot.wave, 'feintThenSpike');
    const spikeType: EnemyType = weaknesses.turtle > weaknesses.exposedRecruitment ? 'TributeAct' : 'StreamingMogul';
    const spikeCount = this.spawnCount(spikeType, spikeBudget, snapshot.wave, 'feintThenSpike');

    if (feintCount > 0) {
      this.recordSpawnEdge(feintEdge);
      commands.push({ type: 'spawn', enemyType: 'GenZSwarm', count: Math.min(3, feintCount), fromEdge: feintEdge });
    }
    if (spikeCount > 0) {
      this.recordSpawnEdge(spikeEdge);
      commands.push({ type: 'spawn', enemyType: spikeType, count: spikeCount, fromEdge: spikeEdge });
    }
    return commands;
  }

  private planBudget(snapshot: BattleSnapshot, weaknesses: PlayerWeaknessReport): number {
    const collapseMultiplier = weaknesses.collapseRisk > 25 ? 0.55 : 1;
    const floatMultiplier = weaknesses.floatingResources > 18 ? 1.12 : 1;
    const baseShare =
      snapshot.wave <= this.tuning.earlyGraceWaves ? this.tuning.earlySpawnBudgetShare : this.tuning.spawnBudgetShare;
    return Math.floor(snapshot.resources.enemyBudget * baseShare * collapseMultiplier * floatMultiplier);
  }

  private pickPlan(plans: ScoredPlan[], tick: number): ScoredPlan | null {
    const viable = plans.filter((plan) => plan.score > 0);
    if (viable.length === 0) return null;

    const rng = mulberry32(hashTick(tick));
    const topThree = viable.slice(0, 3);
    const roll = rng();
    if (roll < 0.6) return topThree[0];
    if (roll < 0.85) return topThree[1] ?? topThree[0];
    return topThree[2] ?? topThree[0];
  }

  private spawnCount(enemyType: EnemyType, budget: number, wave: number, plan: StrategicPlan): number {
    if (wave <= this.tuning.earlyGraceWaves && (enemyType === 'TributeAct' || enemyType === 'StreamingMogul')) {
      return 0;
    }

    const cost = ENEMY_COST[enemyType];
    const affordable = Math.floor(budget / cost);
    if (affordable <= 0) return 0;

    const capByType: Record<EnemyType, number> = {
      GenZSwarm: 4 + wave * 2,
      StreamingMogul: 1 + Math.floor(wave / 3),
      BadManager: 1 + Math.floor(wave / 4),
      TributeAct: 1 + Math.floor(wave / 3),
      MusicPress: 1,
      FilePirate: 1 + Math.floor(wave / 4),
      RivalVenueBuilder: 1,
    };

    const planBoost = plan === 'stallSongDrop' || plan === 'bleedEconomy' || plan === 'splitAttention' ? 1 : 0;
    return Math.max(1, Math.min(affordable, capByType[enemyType] + planBoost));
  }

  private onboardingPressure(snapshot: BattleSnapshot): {
    softPressure: number;
    heavyPressure: number;
    trickPressure: number;
  } {
    if (snapshot.wave <= 0) {
      return { softPressure: 0.35, heavyPressure: 0, trickPressure: 0 };
    }
    if (snapshot.wave <= this.tuning.earlyGraceWaves) {
      return { softPressure: 0.65, heavyPressure: 0.15, trickPressure: 0.2 };
    }
    if (snapshot.wave === this.tuning.earlyGraceWaves + 1) {
      return { softPressure: 0.9, heavyPressure: 0.65, trickPressure: 0.55 };
    }
    return { softPressure: 1, heavyPressure: 1, trickPressure: 1 };
  }

  private chooseSpawnEdge(
    snapshot: BattleSnapshot,
    target: BattleBuildingSnapshot | BattleUnitSnapshot | null,
    plan: StrategicPlan,
    avoidEdge?: SpawnEdge,
  ): SpawnEdge {
    const rng = mulberry32(hashTick(snapshot.tick + snapshot.wave * 101));
    const scored = EDGES.map((edge) => {
      const defenderPenalty = this.edgeDefenderPressure(snapshot, edge);
      const watchedPenalty = this.edgeVisibility(snapshot, edge) * this.tuning.watchedEdgePenalty;
      const repeatPenalty = this.edgeMemoryPenalty(edge);
      const avoidPenalty = edge === avoidEdge ? 999 : 0;
      const targetBias = target ? this.edgeTargetBias(edge, target, snapshot) : 0;
      const flankBonus =
        plan === 'huntRecruiters' || plan === 'bleedEconomy' || plan === 'splitAttention' ? rng() * 12 : rng() * 6;
      return {
        edge,
        score: targetBias + flankBonus - defenderPenalty - watchedPenalty - repeatPenalty - avoidPenalty,
      };
    }).sort((a, b) => b.score - a.score);

    return scored[0].edge;
  }

  private bestTargetForUnit(
    snapshot: BattleSnapshot,
    unit: BattleUnitSnapshot,
  ): BattleBuildingSnapshot | BattleUnitSnapshot | null {
    switch (unit.type) {
      case 'GenZSwarm':
        return this.bestMiningFan(snapshot) ?? this.bestPlayerUnit(snapshot, ['fan']) ?? this.bestBuilding(snapshot, ['FlyerPost', 'Pub', 'MerchStall']);
      case 'StreamingMogul':
        return this.bestBuilding(snapshot, ['FutureBeacon', 'CommunityRadioMast']);
      case 'BadManager':
        return this.bestPlayerUnit(snapshot, ['promoter']) ?? this.bestMiningFan(snapshot) ?? this.bestPlayerUnit(snapshot, ['fan']);
      case 'TributeAct':
        return this.bestBuilding(snapshot, ['RehearsalSpace', 'FutureBeacon']);
      default:
        return this.findBuilding(snapshot, 'FutureBeacon');
    }
  }

  private bestBuilding(snapshot: BattleSnapshot, types: string[]): BattleBuildingSnapshot | null {
    const options = snapshot.buildings.filter((building) => types.includes(building.type));
    return this.lowestRiskTarget(snapshot, options);
  }

  private bestPlayerUnit(snapshot: BattleSnapshot, types: string[]): BattleUnitSnapshot | null {
    const options = snapshot.playerUnits.filter((unit) => types.includes(unit.type));
    return this.lowestRiskTarget(snapshot, options);
  }

  private lowestRiskTarget<T extends BattleBuildingSnapshot | BattleUnitSnapshot>(
    snapshot: BattleSnapshot,
    options: T[],
  ): T | null {
    if (options.length === 0) return null;
    return options
      .slice()
      .sort((a, b) => this.exposureScore(snapshot, b) - this.exposureScore(snapshot, a))[0];
  }

  private findBuilding(snapshot: BattleSnapshot, type: string): BattleBuildingSnapshot | null {
    return snapshot.buildings.find((building) => building.type === type) ?? null;
  }

  private bestMiningFan(snapshot: BattleSnapshot): BattleUnitSnapshot | null {
    const options = snapshot.playerUnits.filter((unit) => unit.type === 'fan' && unit.isMining);
    return this.lowestRiskTarget(snapshot, options);
  }

  private bestExpansionTarget(snapshot: BattleSnapshot): BattleBuildingSnapshot | BattleUnitSnapshot | null {
    return this.bestMiningFan(snapshot) ??
      this.bestPlayerUnit(snapshot, ['fan']) ??
      this.bestBuildSite(snapshot) ??
      this.bestGearSite(snapshot) ??
      this.bestClaimedVenue(snapshot);
  }

  private bestClaimedVenue(snapshot: BattleSnapshot): BattleBuildingSnapshot | null {
    const venues = (snapshot.venues ?? []).filter((venue) => venue.state === 'claimed');
    return this.lowestRiskTarget(snapshot, venues);
  }

  private bestBuildSite(snapshot: BattleSnapshot): BattleBuildingSnapshot | null {
    const sites = (snapshot.buildSites ?? [])
      .filter((site) => !site.completed)
      .map((site) => ({
        id: site.id,
        type: 'FanBuildSite',
        col: site.col,
        row: site.row,
        hp: site.gearProgress,
        maxHp: Math.max(site.gearRequired, 1),
      }));
    return this.lowestRiskTarget(snapshot, sites);
  }

  private bestGearSite(snapshot: BattleSnapshot): BattleBuildingSnapshot | null {
    const sites = (snapshot.gearSites ?? [])
      .filter((site) => site.active || (site.fanCount ?? 0) > 0)
      .map((site) => ({
        id: site.id,
        type: 'GearSite',
        col: site.col,
        row: site.row,
        hp: site.fanCount ?? 1,
        maxHp: 3,
      }));
    return this.lowestRiskTarget(snapshot, sites);
  }

  private exposureScore(
    snapshot: BattleSnapshot,
    target: BattleBuildingSnapshot | BattleUnitSnapshot | null,
  ): number {
    if (!target) return 0;
    const nearbyDefenders = snapshot.playerUnits.filter((unit) => distance(unit, target) <= 5);
    const defenderStrength = nearbyDefenders.reduce((sum, unit) => sum + this.unitThreat(unit), 0);
    const hpRatio = target.maxHp ? target.hp / target.maxHp : 1;
    return 28 - defenderStrength + Math.max(0, 1 - hpRatio) * 24;
  }

  private isolationScore(snapshot: BattleSnapshot, target: BattleUnitSnapshot): number {
    const protectors = snapshot.playerUnits.filter((unit) => unit.type === 'protector');
    const mystics = snapshot.playerUnits.filter((unit) => unit.type === 'mystic');
    const nearestProtector = nearestDistance(target, protectors);
    const nearestMystic = nearestDistance(target, mystics);
    return Math.min(24, nearestProtector * 2) + Math.min(18, nearestMystic * 1.5);
  }

  private expansionSitePressure(snapshot: BattleSnapshot, target: GridPosition | null): number {
    if (!target) return 0;
    return this.exposureScore(snapshot, {
      id: 'expansion',
      type: 'ExpansionSite',
      col: target.col,
      row: target.row,
      hp: 1,
      maxHp: 1,
    });
  }

  private fanbasePressure(snapshot: BattleSnapshot): number {
    const { fanbase, fanbaseCap } = snapshot.resources;
    if (fanbase === undefined || fanbaseCap === undefined || fanbaseCap <= 0) return 0;
    return fanbase / fanbaseCap > 0.75 ? 18 : 0;
  }

  private mogulNeed(snapshot: BattleSnapshot): number {
    const moguls = snapshot.enemyUnits.filter((unit) => unit.type === 'StreamingMogul').length;
    return Math.max(0, 2 - moguls) * 16;
  }

  private fogOpportunity(snapshot: BattleSnapshot): number {
    const edgeVisibility = snapshot.visibility?.edgeVisibility;
    if (!edgeVisibility) return 0;
    const leastWatched = Math.min(...EDGES.map((edge) => this.edgeVisibility(snapshot, edge)));
    return (1 - leastWatched) * this.tuning.hiddenPressureBonus;
  }

  private hiddenPressure(snapshot: BattleSnapshot): number {
    if (snapshot.enemyUnits.length === 0) return 0;
    const hiddenCount = snapshot.enemyUnits.filter((unit) => unit.visibleToPlayer === false).length;
    return (hiddenCount / snapshot.enemyUnits.length) * this.tuning.hiddenPressureBonus;
  }

  private edgeDefenderPressure(snapshot: BattleSnapshot, edge: SpawnEdge): number {
    return snapshot.playerUnits.reduce(
      (sum, unit) => sum + this.unitThreat(unit) * edgeAffinity(edge, unit, snapshot.map),
      0,
    );
  }

  private edgeTargetBias(edge: SpawnEdge, target: GridPosition, snapshot: BattleSnapshot): number {
    return edgeAffinity(edge, target, snapshot.map) * 20;
  }

  private unitThreat(unit: BattleUnitSnapshot): number {
    switch (unit.type) {
      case 'protector':
        return 10;
      case 'mystic':
        return 8;
      case 'engine':
        return 5;
      default:
        return 3;
    }
  }

  private edgeVisibility(snapshot: BattleSnapshot, edge: SpawnEdge): number {
    const raw = snapshot.visibility?.edgeVisibility?.[edge];
    if (raw === undefined) return 0;
    return Math.max(0, Math.min(1, raw));
  }

  private edgeMemoryPenalty(edge: SpawnEdge): number {
    const repeatPenalty = edge === this.lastSpawnEdge ? this.tuning.edgeRepeatPenalty : 0;
    const memoryPenalty =
      this.recentSpawnEdges.filter((recentEdge) => recentEdge === edge).length * this.tuning.adaptationMemory;
    return repeatPenalty + memoryPenalty;
  }

  private repeatPlanPenalty(plan: StrategicPlan): number {
    return this.lastPlan === plan ? 7 : 0;
  }

  private recordSpawnEdge(edge: SpawnEdge): void {
    this.lastSpawnEdge = edge;
    this.recentSpawnEdges.push(edge);
    if (this.recentSpawnEdges.length > 6) {
      this.recentSpawnEdges.shift();
    }
  }

  private mostWatchedEdge(snapshot: BattleSnapshot): SpawnEdge | null {
    if (!snapshot.visibility?.edgeVisibility) return null;
    return EDGES.slice().sort((a, b) => this.edgeVisibility(snapshot, b) - this.edgeVisibility(snapshot, a))[0];
  }
}

function distance(a: GridPosition, b: GridPosition): number {
  return Math.hypot(a.col - b.col, a.row - b.row);
}

function nearestDistance(target: GridPosition, units: BattleUnitSnapshot[]): number {
  if (units.length === 0) return 12;
  return Math.min(...units.map((unit) => distance(target, unit)));
}

function edgeAffinity(
  edge: SpawnEdge,
  pos: GridPosition,
  map: BattleSnapshot['map'] = { width: 40, height: 30 },
): number {
  const width = Math.max(1, map.width);
  const height = Math.max(1, map.height);
  switch (edge) {
    case 'north':
      return Math.max(0, 1 - pos.row / height);
    case 'south':
      return Math.max(0, pos.row / height);
    case 'west':
      return Math.max(0, 1 - pos.col / width);
    case 'east':
      return Math.max(0, pos.col / width);
  }
}

function clampScore(value: number): number {
  return Math.max(0, Math.min(40, value));
}

function hashTick(tick: number): number {
  let value = tick | 0;
  value ^= value << 13;
  value ^= value >>> 17;
  value ^= value << 5;
  return value >>> 0;
}

function mulberry32(seed: number): () => number {
  let value = seed >>> 0;
  return () => {
    value += 0x6d2b79f5;
    let t = value;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
